import { Button, useActionsBtnContext } from '@finbeat/react-toolkit';
import { requestDocs } from 'axios/clientApi';
import { approveRepeatClient } from 'axios/orderApi';
import { CallToClient } from 'base-components/WidgetCrm/WidgetClass';
import OrderCancelPopover from 'components/Workplaces/Order/Popovers/OrderCancelPopover';
import OrderPostponePopover from 'components/Workplaces/Order/Popovers/OrderPostponePopover';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { OrderDetailsGeneralInfo } from 'models/api/order';
import { useOrderInfoContext } from 'providers/OrderInfoProvider';
import { RefObject } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { Spinner } from 'reactstrap';
import { useOrderWorkplaceState } from '../../Order';
import '../OrderInfo.scss';

const ControlButtons = ({
    info,
    modalRef,
    cancelAction,
    onNextSubmit,
}: {
    info: OrderDetailsGeneralInfo;
    modalRef?: RefObject<HTMLElement>;
    cancelAction: (dirty?: boolean) => void;
    onNextSubmit?: () => void;
}) => {
    const className = 'order-control-buttons';
    const api = useApi();
    const queryClient = useQueryClient();
    const { hasRole } = useAuth();
    const { id, orderGeneral } = info;
    const { requestedDocs, orderDetails, isDirty, submitOrderMutation } = useOrderInfoContext();
    const { addRequest } = useActionsBtnContext();
    const { t } = useTranslation();
    const { fetchCurrentItem } = useOrderWorkplaceState();

    const requestDocsMutation = useMutation(
        'requestDocsMutation',
        () => requestDocs(api, info.orderGeneral.clientId, info.id, requestedDocs),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['orderCounters']);
                queryClient.invalidateQueries([['order']]);
                cancelAction(false);
            },
        }
    );

    const clientRepeatApprovalMutation = useMutation(
        'clientRepeatApprovalMutation',
        () => approveRepeatClient(api, info.id),
        {
            onSuccess: () => {
                fetchCurrentItem();
                queryClient.invalidateQueries(['clientInfo']);
                queryClient.invalidateQueries(['orderInfo']);
                queryClient.invalidateQueries(['orderCounters']);
                queryClient.invalidateQueries([['order']]);
            },
        }
    );

    return (
        <>
            <div className={`${className}_left`}>
                {(orderGeneral.canPassToScoring || orderGeneral.statusCode === 'checkdocs') &&
                    hasRole('orderSubmit') &&
                    (submitOrderMutation.isLoading ? (
                        <Spinner />
                    ) : (
                        <Button
                            color="success"
                            className="float-left"
                            onClick={() => {
                                if (onNextSubmit) {
                                    onNextSubmit();
                                } else {
                                    submitOrderMutation.mutate(
                                        orderGeneral.canPassToScoring ? 'next' : 'checkdocs'
                                    );
                                }
                            }}
                        >
                            <Trans i18nKey="BackOfficeNext" />
                        </Button>
                    ))}
            </div>

            <div className={`${className}_right`}>
                {orderGeneral.statusCode === 'transftooper' && hasRole('orderSubmit') && (
                    <Button
                        color="success"
                        onClick={() => submitOrderMutation.mutate('to-verifier')}
                    >
                        <Trans i18nKey="BackOfficePassToVerifier" />
                    </Button>
                )}
                {orderGeneral.statusCode === 'workcallcenter' && hasRole('clientApproveRepeat') && (
                    <Button
                        className='btn-purple'
                        size="sm"
                        disabled={clientRepeatApprovalMutation.isLoading}
                        onClick={() => {
                            addRequest({
                                content: t('BackOfficeAreYouSure'),
                                confirmCallback: (res) => {
                                    if (res) {
                                        clientRepeatApprovalMutation.mutate();
                                    }
                                },
                            })
                        }}
                        outline
                    >
                        <Trans i18nKey="BackOfficeApproveClient" />
                    </Button>
                )}
                <Button
                    color="info"
                    onClick={() => {
                        CallToClient(orderDetails?.orderGeneral.clientId);
                    }}
                    outline
                    size="sm"
                >
                    <Trans i18nKey="BackOfficeCallToClient" />
                </Button>

                {orderGeneral.canUpdDocs &&
                    requestedDocs.length > 0 &&
                    hasRole('clientsRequestDocuments') && (
                        <Button
                            color="warning"
                            onClick={() => requestDocsMutation.mutate()}
                            outline
                            size="sm"
                        >
                            <Trans i18nKey="BackOfficeRequestDocuments" />
                        </Button>
                    )}

                {!orderGeneral.isFinal && hasRole('orderCancel') && (
                    <OrderCancelPopover
                        orderId={id.toString()}
                        key={`cancel-order-${id}`}
                        withIcon={false}
                        popoverBtn={false}
                        buttonProps={{
                            outline: true,
                            color: 'danger',
                            size: 'sm',
                        }}
                        popoverContainer={modalRef}
                        currentStatus={orderDetails?.orderGeneral.statusCode ?? ''}
                        mutateCallback={() => cancelAction(false)}
                    />
                )}

                {orderGeneral.canEditOrder && (
                    <>
                        {orderGeneral.statusCode !== 'postpone' && hasRole('orderPostpone') && (
                            <OrderPostponePopover
                                orderId={id.toString()}
                                key={`cancel-order-${id}`}
                                withIcon={false}
                                popoverBtn={false}
                                buttonProps={{
                                    outline: true,
                                    color: 'secondary',
                                    size: 'sm',
                                }}
                                popoverContainer={modalRef}
                                currentStatus={orderDetails?.orderGeneral.statusCode ?? ''}
                            />
                        )}
                        {hasRole('orderSubmit') && (
                            <Button
                                color="success"
                                onClick={() => submitOrderMutation.mutate('save')}
                            >
                                <Trans i18nKey="BackOfficeSave" />
                            </Button>
                        )}
                    </>
                )}
                <Button color="secondary" onClick={() => cancelAction(isDirty)}>
                    <Trans i18nKey="BackOfficeCancel" />
                </Button>
            </div>
        </>
    );
};

export default ControlButtons;
